/*
 * PLP v2 — filters, sort, drawer and SEE MORE for sections/main-collection-v2.liquid.
 *
 * Every change fetches the section through the Section Rendering API
 * (?section_id=…) and swaps three regions: [data-plp-toolbar],
 * [data-plp-drawer-body] and [data-plp-results]. Open dropdowns/accordions
 * (data-plp-key) and the drawer's open state survive the swap.
 *
 * Quick add needs nothing here: store.js listens for .quickBuy clicks on
 * document.body, so cards inserted later work as they are.
 *
 * Back to top ([data-plp-btt]): shown once product N has scrolled out of view
 * above the viewport (IntersectionObserver, no scroll listener unless
 * "only while scrolling up" is on). Re-attached after every filter/sort swap.
 *
 * Promo videos below the first rows carry data-plp-video-src and get their src when they come
 * within ~500px of the viewport (watchVideos), also re-run after every swap.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-plp]');
  if (!root) return;

  var sectionId = root.getAttribute('data-section-id');
  var collectionUrl = root.getAttribute('data-collection-url');
  var drawer = root.querySelector('[data-plp-drawer]');
  var requestId = 0;

  // Lift the drawer to <body> so no ancestor's stacking context (sticky
  // header, transformed wrappers) can sit above it. It carries the .plp class
  // itself, so its styles still apply.
  document.body.appendChild(drawer);
  var lastFocus = null;

  // Back-to-top hooks; real implementations are assigned below if the button exists.
  var bttWatch = function () {};
  var bttUpdate = function () {};

  /* ---------- URL building ---------- */

  function currentSort() {
    return new URLSearchParams(window.location.search).get('sort_by');
  }

  // The drawer holds every filter (the toolbar leaves out Price), so it is the
  // single source of truth for the query string.
  function buildUrl(sortOverride) {
    var params = new URLSearchParams();
    var body = drawer.querySelector('[data-plp-drawer-body]');
    body.querySelectorAll('[data-plp-filter]').forEach(function (input) {
      if (input.type === 'checkbox') {
        if (input.checked) params.append(input.name, input.value);
      } else if (input.value !== '') {
        params.append(input.name, input.value);
      }
    });
    var sort = sortOverride || currentSort();
    if (sort) params.set('sort_by', sort);
    var qs = params.toString();
    return collectionUrl + (qs ? '?' + qs : '');
  }

  function sectionUrl(url) {
    var u = new URL(url, window.location.origin);
    u.searchParams.set('section_id', sectionId);
    return u.toString();
  }

  /* ---------- Rendering ---------- */

  function openKeys() {
    var keys = [];
    document.querySelectorAll('details[data-plp-key][open]').forEach(function (d) {
      keys.push(d.getAttribute('data-plp-key'));
    });
    return keys;
  }

  function restoreKeys(keys, scope) {
    scope.querySelectorAll('details[data-plp-key]').forEach(function (d) {
      if (keys.indexOf(d.getAttribute('data-plp-key')) !== -1) d.open = true;
    });
  }

  function fetchSection(url) {
    return fetch(sectionUrl(url), { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        return new DOMParser().parseFromString(html, 'text/html');
      });
  }

  // Links rendered inside a ?section_id= response can carry that parameter;
  // followed without JS it would load a bare section, so strip it.
  function cleanLinks() {
    document.querySelectorAll('[data-plp-url], [data-plp-next]').forEach(function (a) {
      var href = a.getAttribute('href');
      if (!href || href.indexOf('section_id') === -1) return;
      var u = new URL(href, window.location.origin);
      u.searchParams.delete('section_id');
      a.setAttribute('href', u.pathname + u.search);
    });
  }

  function refreshWidgets() {
    cleanLinks();
    bttWatch();
    watchVideos();
    if (window.okeWidgetApi && typeof window.okeWidgetApi.initAllWidgets === 'function') {
      window.okeWidgetApi.initAllWidgets();
    }
  }

  function render(url) {
    var id = ++requestId;
    var keys = openKeys();
    // Toolbar dropdowns close after a choice; drawer accordions stay as they were.
    keys = keys.filter(function (k) { return k.indexOf('dd-') !== 0; });
    root.classList.add('is-loading');

    return fetchSection(url)
      .then(function (doc) {
        if (id !== requestId) return; // a newer request superseded this one
        ['[data-plp-toolbar]', '[data-plp-drawer-body]', '[data-plp-results]'].forEach(function (sel) {
          var next = doc.querySelector(sel);
          var current = document.querySelector(sel);
          if (next && current) current.innerHTML = next.innerHTML;
        });
        restoreKeys(keys, document);
        history.replaceState({}, '', url);
        refreshWidgets();
      })
      .catch(function () {
        window.location.href = url;
      })
      .finally(function () {
        if (id === requestId) root.classList.remove('is-loading');
      });
  }

  /* ---------- Load more ---------- */

  function loadMore(link) {
    var url = link.getAttribute('href');
    link.setAttribute('aria-busy', 'true');
    link.classList.add('is-loading');

    fetchSection(url)
      .then(function (doc) {
        var grid = root.querySelector('[data-plp-grid]');
        var nextGrid = doc.querySelector('[data-plp-grid]');
        if (grid && nextGrid) {
          var firstNew = nextGrid.firstElementChild;
          while (nextGrid.firstElementChild) grid.appendChild(nextGrid.firstElementChild);
          // Move focus to the first new card so keyboard users continue from there.
          var focusTarget = firstNew && firstNew.querySelector('.plp-card__title a, .plp-promo__link');
          if (focusTarget) focusTarget.focus({ preventScroll: true });
        }
        var more = root.querySelector('[data-plp-more]');
        var nextMore = doc.querySelector('[data-plp-more]');
        if (more && nextMore) more.innerHTML = nextMore.innerHTML;
        refreshWidgets();
      })
      .catch(function () {
        window.location.href = url;
      });
  }

  /* ---------- Drawer ---------- */

  function openDrawer() {
    lastFocus = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('plp-drawer-open');
    bttUpdate();
    var panel = drawer.querySelector('.plp-drawer__panel');
    if (panel) panel.focus();
  }

  function closeDrawer() {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('plp-drawer-open');
    bttUpdate();
    if (lastFocus && document.contains(lastFocus)) lastFocus.focus();
  }

  /* ---------- Back to top ---------- */

  (function initBackToTop() {
    var btt = root.querySelector('[data-plp-btt]');
    if (!btt) return;

    // Out of any ancestor that could trap position: fixed (transforms, stacking contexts).
    document.body.appendChild(btt);

    var after = parseInt(btt.getAttribute('data-plp-btt-after'), 10) || 8;
    var target = btt.getAttribute('data-plp-btt-target') || 'toolbar';
    var upOnly = btt.hasAttribute('data-plp-btt-up-only');
    var pastThreshold = false;
    var scrollingUp = false;
    var observer = null;

    bttUpdate = function () {
      var show = pastThreshold &&
        (!upOnly || scrollingUp) &&
        !document.documentElement.classList.contains('plp-drawer-open');
      btt.classList.toggle('is-visible', show);
      btt.setAttribute('aria-hidden', show ? 'false' : 'true');
      btt.tabIndex = show ? 0 : -1;
    };

    // Watch product card N (promo tiles don't count). Past it = it left the viewport upwards.
    bttWatch = function () {
      if (observer) observer.disconnect();
      pastThreshold = false;
      var cards = root.querySelectorAll('[data-plp-grid] > .plp-grid__item:not(.plp-promo)');
      var card = cards[after - 1];
      if (card && 'IntersectionObserver' in window) {
        observer = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            pastThreshold = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          });
          bttUpdate();
        });
        observer.observe(card);
      }
      bttUpdate();
    };

    if (upOnly) {
      var lastY = window.scrollY;
      var ticking = false;
      window.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          var y = window.scrollY;
          if (Math.abs(y - lastY) > 4) scrollingUp = y < lastY;
          lastY = y;
          ticking = false;
          bttUpdate();
        });
      }, { passive: true });
    }

    // A sticky/fixed header would cover the filter bar after the scroll; leave room for it.
    function headerOffset() {
      var header = document.querySelector('.shopify-section-group-header-group, .page-header');
      if (!header) return 0;
      var pos = window.getComputedStyle(header).position;
      return pos === 'sticky' || pos === 'fixed' ? header.getBoundingClientRect().height : 0;
    }

    btt.addEventListener('click', function () {
      var y = 0;
      var toolbar = root.querySelector('[data-plp-toolbar]');
      if (target === 'toolbar' && toolbar) {
        y = toolbar.getBoundingClientRect().top + window.scrollY - headerOffset();
      }
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: Math.max(0, y), behavior: reduce ? 'auto' : 'smooth' });

      // Keyboard users continue from the filter bar (or the page title), not from the bottom.
      var focusEl = target === 'toolbar' && toolbar
        ? toolbar.querySelector('button, summary, a[href]')
        : root.querySelector('.plp-hero__title');
      if (focusEl) {
        if (!focusEl.matches('button, summary, a[href]')) focusEl.setAttribute('tabindex', '-1');
        focusEl.focus({ preventScroll: true });
      }

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'plp_back_to_top',
        plp_target: target,
        plp_products_loaded: root.querySelectorAll('[data-plp-grid] > .plp-grid__item:not(.plp-promo)').length,
        plp_collection: collectionUrl
      });
    });

    bttWatch();
  })();

  /* ---------- Promo videos: load when near the viewport ---------- */

  // plp-promo-tile.liquid gives videos below the first rows data-plp-video-src instead of src.
  // Re-run after every swap / SEE MORE so new tiles are picked up.
  var videoObserver = null;
  function loadVideo(video) {
    var src = video.getAttribute('data-plp-video-src');
    if (!src) return;
    video.removeAttribute('data-plp-video-src');
    video.src = src;
    var playing = video.play();
    if (playing && playing.catch) playing.catch(function () {});
  }
  function watchVideos() {
    var videos = root.querySelectorAll('video[data-plp-video-src]');
    if (!videos.length) return;
    if (!('IntersectionObserver' in window)) {
      videos.forEach(loadVideo);
      return;
    }
    if (!videoObserver) {
      videoObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          videoObserver.unobserve(entry.target);
          loadVideo(entry.target);
        });
      }, { rootMargin: '500px 0px' });
    }
    videos.forEach(function (v) { videoObserver.observe(v); });
  }
  watchVideos();

  /* ---------- Events ---------- */

  var priceTimer;

  function onChange(e) {
    var input = e.target;

    if (input.matches('[data-plp-sort-radio]')) {
      render(buildUrl(input.value));
      return;
    }
    if (!input.matches('[data-plp-filter]')) return;

    // Mirror a toolbar checkbox into the drawer (and vice versa) before reading.
    if (input.type === 'checkbox') {
      document.querySelectorAll('[data-plp-filter][type="checkbox"]').forEach(function (other) {
        if (other !== input && other.name === input.name && other.value === input.value) {
          other.checked = input.checked;
        }
      });
      render(buildUrl());
    } else {
      clearTimeout(priceTimer);
      priceTimer = setTimeout(function () { render(buildUrl()); }, 500);
    }
  }

  function onClick(e) {
    var t = e.target;

    var opener = t.closest('[data-plp-drawer-open]');
    if (opener) { e.preventDefault(); openDrawer(); return; }

    var closer = t.closest('[data-plp-drawer-close]');
    if (closer) { e.preventDefault(); closeDrawer(); return; }

    var sortBtn = t.closest('[data-plp-sort]');
    if (sortBtn) { e.preventDefault(); render(buildUrl(sortBtn.getAttribute('data-plp-sort'))); return; }

    var link = t.closest('[data-plp-url]');
    if (link) { e.preventDefault(); render(link.getAttribute('href')); return; }

    var next = t.closest('[data-plp-next]');
    if (next) { e.preventDefault(); loadMore(next); return; }
  }

  [root, drawer].forEach(function (el) {
    el.addEventListener('change', onChange);
    el.addEventListener('click', onClick);
  });

  // Only one toolbar dropdown open at a time; close them on an outside click.
  root.addEventListener('toggle', function (e) {
    var d = e.target;
    if (!d.matches || !d.matches('.plp-dd') || !d.open) return;
    root.querySelectorAll('.plp-dd[open]').forEach(function (other) {
      if (other !== d) other.open = false;
    });
  }, true);

  document.addEventListener('click', function (e) {
    if (e.target.closest('.plp-dd')) return;
    root.querySelectorAll('.plp-dd[open]').forEach(function (d) { d.open = false; });
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    if (drawer.classList.contains('is-open')) { closeDrawer(); return; }
    var openDd = root.querySelector('.plp-dd[open]');
    if (openDd) {
      openDd.open = false;
      var summary = openDd.querySelector('summary');
      if (summary) summary.focus();
    }
  });
})();
